import React, { useState } from 'react';
import { Button, Input, TextArea, Select, Card, Badge, Avatar, Divider, Modal, Switch, EmptyState, Alert, Logo } from '../components/components';
import { Sidebar, Header, Footer, useWindowWidth } from '../layout/layouts';
import { theme } from '../styles/theme';

export interface WorkspaceProps {
  initialTab?: string;
}

export const Workspace: React.FC<WorkspaceProps> = ({ initialTab = 'dashboard' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < 1024;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // State for mock business actions
  const [leads, setLeads] = useState([
    { name: 'Karthik Raja', email: 'karthik@outlook.com', phone: '+91 9845012345', status: 'NEW' },
    { name: 'Srinivas Rao', email: 'srinivas@gmail.com', phone: '+91 9123456789', status: 'CONTACTED' },
    { name: 'Divya Reddy', email: 'divya@reddy.co', phone: '+91 8877665544', status: 'ENROLLED' }
  ]);
  const [courses, setCourses] = useState([
    { title: 'IIT-JEE Physics Prep Core', fee: '₹12,500', status: 'PUBLISHED', stream: 'Widevine' },
    { title: 'UPSC General Studies Advanced', fee: '₹18,000', status: 'DRAFT', stream: 'AES-128' }
  ]);
  const [newLeadName, setNewLeadName] = useState('');
  const [newLeadEmail, setNewLeadEmail] = useState('');

  const handleAddLead = (e: React.FormEvent) => {
    e.preventDefault();
    setLeads([...leads, { name: newLeadName, email: newLeadEmail, phone: '+91 9900112233', status: 'NEW' }]);
    setNewLeadName('');
    setNewLeadEmail('');
    setIsAddModalOpen(false);
  };

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      backgroundColor: isDarkMode ? '#111827' : theme.colors.background,
      flexDirection: 'row',
      position: 'relative'
    }}>
      {/* Sidebar - responsive behavior */}
      {isMobile ? (
        <>
          {/* Overlay background when mobile sidebar is open */}
          {isSidebarOpen && (
            <div 
              onClick={() => setIsSidebarOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: theme.zIndex.fixed - 1,
              }}
            />
          )}
          {/* Sidebar sliding container */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: isSidebarOpen ? 0 : '-260px',
            width: '260px',
            height: '100vh',
            zIndex: theme.zIndex.fixed,
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <Sidebar 
              activeTab={activeTab} 
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsSidebarOpen(false);
              }} 
              userRole="OWNER" 
            />
          </div>
        </>
      ) : (
        <Sidebar activeTab={activeTab} onTabChange={setActiveTab} userRole="OWNER" />
      )}
      
      <main style={{ 
        flex: 1, 
        padding: isMobile ? theme.spacing.md : theme.spacing.lg, 
        display: 'flex', 
        flexDirection: 'column', 
        boxSizing: 'border-box', 
        overflowX: 'hidden',
        width: '100%'
      }}>
        {/* Mobile Header / Menu Bar */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing.sm,
            backgroundColor: isDarkMode ? '#1f2937' : theme.colors.surface,
            borderBottom: `1px solid ${isDarkMode ? '#374151' : theme.colors.border}`,
            marginBottom: theme.spacing.md,
            borderRadius: theme.radius.md,
            boxShadow: theme.shadows.sm,
          }}>
            <Button variant="outline" size="sm" onClick={() => setIsSidebarOpen(true)} style={{ padding: '0 12px', minHeight: '40px' }}>
              ☰ Menu
            </Button>
            <div style={{ transform: 'scale(0.85)' }}>
              <Logo size="sm" theme={isDarkMode ? 'dark' : 'light'} />
            </div>
          </div>
        )}
        
        {/* Render Tab Contents */}
        {activeTab === 'dashboard' && (
          <div>
            <Header 
              title="Workplace Command Center" 
              subtitle="Overview of your academy analytics, active lessons, and launch checklist."
              action={!isMobile && <Button onClick={() => window.location.href = '/'}>View Live Website 🌐</Button>}
            />

            {isMobile && (
              <div style={{ marginBottom: theme.spacing.md }}>
                <Button fullWidth onClick={() => window.location.href = '/'}>View Live Website 🌐</Button>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: theme.spacing.md, marginBottom: theme.spacing.xl }}>
              <Card elevation="sm">
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Active Students</span>
                <h2 style={{ fontSize: '28px', margin: '4px 0', color: theme.colors.primary }}>342</h2>
                <Badge variant="success">+12% this month</Badge>
              </Card>
              <Card elevation="sm">
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Collections (INR)</span>
                <h2 style={{ fontSize: '28px', margin: '4px 0', color: theme.colors.primary }}>₹4,85,900</h2>
                <Badge variant="primary">Target met</Badge>
              </Card>
              <Card elevation="sm">
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Database Sync Status</span>
                <h2 style={{ fontSize: '28px', margin: '4px 0', color: theme.colors.accent }}>99.9%</h2>
                <Badge variant="success">Fully Synced</Badge>
              </Card>
              <Card elevation="sm">
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>DRM Stream Logs</span>
                <h2 style={{ fontSize: '28px', margin: '4px 0', color: theme.colors.primary }}>1,284 h</h2>
                <Badge variant="info">Widevine active</Badge>
              </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: theme.spacing.lg }}>
              <Card hoverable={false}>
                <h3>Launch Readiness Checklist</h3>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <span style={{ color: theme.colors.accent }}>✔</span>
                    <span style={{ fontSize: theme.typography.fontSize.sm }}>Firebase SDK Auth integration complete</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <span style={{ color: theme.colors.accent }}>✔</span>
                    <span style={{ fontSize: theme.typography.fontSize.sm }}>Firestore security rules compiled & deployed</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <span style={{ color: theme.colors.accent }}>✔</span>
                    <span style={{ fontSize: theme.typography.fontSize.sm }}>Razorpay payment keys stored securely</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <span style={{ color: '#E11D48' }}>✖</span>
                    <span style={{ fontSize: theme.typography.fontSize.sm }}>Live custom domain SSL setup pending DNS sync</span>
                  </div>
                </div>
              </Card>
              
              <Card hoverable={false}>
                <h3>AI Tutor Assistant Diagnostics</h3>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>Active Sessions Today</span>
                    <strong>128</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>Gemini API Latency</span>
                    <strong>0.84 sec</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>Cost Incurred</span>
                    <Badge variant="info">$2.14 / $50 max</Badge>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'crm' && (
          <div>
            <Header 
              title="CRM Admissions & Leads" 
              subtitle="Track inquiries, follow-ups, and student enrollment pipelines."
              action={<Button onClick={() => setIsAddModalOpen(true)}>Add Inquiry Lead +</Button>}
            />
            
            <Card hoverable={false} style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.colors.border}`, height: '40px' }}>
                    <th style={{ fontSize: theme.typography.fontSize.sm }}>Full Name</th>
                    <th style={{ fontSize: theme.typography.fontSize.sm }}>Email Address</th>
                    <th style={{ fontSize: theme.typography.fontSize.sm }}>Contact Number</th>
                    <th style={{ fontSize: theme.typography.fontSize.sm }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${theme.colors.border}`, height: '56px' }}>
                      <td style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold }}>{lead.name}</td>
                      <td style={{ fontSize: theme.typography.fontSize.sm }}>{lead.email}</td>
                      <td style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>{lead.phone}</td>
                      <td>
                        <Badge variant={lead.status === 'ENROLLED' ? 'success' : lead.status === 'CONTACTED' ? 'info' : 'warning'}>
                          {lead.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Create New CRM Lead">
              <form onSubmit={handleAddLead} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <Input label="Student Full Name" value={newLeadName} onChange={(e) => setNewLeadName(e.target.value)} required />
                <Input type="email" label="Student Email Address" value={newLeadEmail} onChange={(e) => setNewLeadEmail(e.target.value)} required />
                <Button type="submit" fullWidth>Add Lead</Button>
              </form>
            </Modal>
          </div>
        )}

        {activeTab === 'students' && (
          <div>
            <Header title="Enrolled Students Roster" subtitle="Control system device bounds, attendance records, and courses." />
            <Card hoverable={false} style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.colors.border}`, height: '40px', textAlign: 'left' }}>
                    <th>Student Details</th>
                    <th>Subscribed Class</th>
                    <th>Device Bound</th>
                    <th>Roster Access</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: `1px solid ${theme.colors.border}`, height: '64px' }}>
                    <td style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, height: '64px' }}>
                      <Avatar name="Anusha Dodla" />
                      <div>
                        <strong>Anusha Dodla</strong><br/>
                        <span style={{ fontSize: '12px', color: theme.colors.textSecondary }}>anusha@example.com</span>
                      </div>
                    </td>
                    <td>IIT-JEE Physics Prep Core</td>
                    <td><Badge variant="success">Pixel 8 Bound</Badge></td>
                    <td><Button size="sm" variant="outline">Revoke</Button></td>
                  </tr>
                  <tr style={{ borderBottom: `1px solid ${theme.colors.border}`, height: '64px' }}>
                    <td style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, height: '64px' }}>
                      <Avatar name="Kiran Kumar" />
                      <div>
                        <strong>Kiran Kumar</strong><br/>
                        <span style={{ fontSize: '12px', color: theme.colors.textSecondary }}>kiran@example.com</span>
                      </div>
                    </td>
                    <td>UPSC General Studies</td>
                    <td><Badge variant="warning">Awaiting Bound</Badge></td>
                    <td><Button size="sm" variant="primary">Authorize</Button></td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {activeTab === 'faculty' && (
          <div>
            <Header title="Faculty & Instructors" subtitle="Coordinate courses and assign classes to your teaching team." />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: theme.spacing.lg }}>
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.md }}>
                  <Avatar name="Prof. H.C. Verma" size="lg" />
                  <div>
                    <h3 style={{ margin: 0 }}>Prof. H.C. Verma</h3>
                    <span style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Senior Physics Faculty</span>
                  </div>
                </div>
                <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Assigns 2 courses, oversees 148 registered students with active presence rosters.</p>
                <Divider />
                <Button variant="outline" fullWidth>Assign New Lesson</Button>
              </Card>
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.md }}>
                  <Avatar name="Dr. Lakshmi Naidu" size="lg" />
                  <div>
                    <h3 style={{ margin: 0 }}>Dr. Lakshmi Naidu</h3>
                    <span style={{ fontSize: '12px', color: theme.colors.textSecondary }}>UPSC GS Faculty</span>
                  </div>
                </div>
                <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Oversees syllabus delivery and student response analytics.</p>
                <Divider />
                <Button variant="outline" fullWidth>Assign New Lesson</Button>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div>
            <Header title="Academic Course Catalog" subtitle="Upload syllabus files, configure video DRM streaming, and set fees." />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))', gap: theme.spacing.lg }}>
              {courses.map((course, i) => (
                <Card key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.base }}>{course.title}</h3>
                    <Badge variant={course.status === 'PUBLISHED' ? 'success' : 'primary'}>{course.status}</Badge>
                  </div>
                  <h4 style={{ color: theme.colors.primary, margin: '8px 0' }}>Fee: {course.fee}</h4>
                  <Divider />
                  <div style={{ fontSize: '12px', color: theme.colors.textSecondary, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>📹 Stream Protection: <strong>{course.stream} DRM</strong></span>
                    <span>📄 Material Attachment: <strong>JEE-Syllabus.pdf</strong></span>
                  </div>
                  <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.md, flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button variant="outline" style={{ flex: 1 }}>Edit Syllabus</Button>
                    <Button style={{ flex: 1 }}>{course.status === 'PUBLISHED' ? 'Deactivate' : 'Publish Core'}</Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div>
            <Header title="Payments ledger & Invoices" subtitle="Initiate sandbox Razorpay transactions and verify captured invoice webhooks." />
            <Card hoverable={false} style={{ marginBottom: theme.spacing.xl }}>
              <h3>Razorpay Test Simulator (₹1 Sandbox Transaction)</h3>
              <p style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Verify webhook capture sequences and automated invoice generations instantly.</p>
              <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                gap: theme.spacing.md, 
                marginTop: theme.spacing.md, 
                alignItems: isMobile ? 'stretch' : 'center' 
              }}>
                <Input placeholder="Enter Student Email" style={{ maxWidth: isMobile ? '100%' : '300px' }} defaultValue="pilotstudent1@millish.com" />
                <Button onClick={() => alert('Razorpay sandbox window popped. UPI/Card success triggered!')}>Initiate Sandbox Checkout</Button>
              </div>
            </Card>

            <Card hoverable={false} style={{ overflowX: 'auto' }}>
              <h3>Captured Transaction Ledger</h3>
              <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left', marginTop: theme.spacing.md }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.colors.border}`, height: '40px' }}>
                    <th>Payment ID</th>
                    <th>Email Account</th>
                    <th>Amount Capture</th>
                    <th>Receipt Invoice</th>
                    <th>Webhook Log</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: `1px solid ${theme.colors.border}`, height: '56px' }}>
                    <td><strong>pay_xyz987</strong></td>
                    <td>pilotstudent1@millish.com</td>
                    <td>₹1.00</td>
                    <td><span style={{ color: theme.colors.primary, cursor: 'pointer', textDecoration: 'underline' }}>Download PDF</span></td>
                    <td><Badge variant="success">captured</Badge></td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <Header title="Performance Analytics & Telemetry" subtitle="Monitor device response latency, cold start logs, and live app traces." />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: theme.spacing.lg }}>
              <Card hoverable={false}>
                <h3>Average Diagnostics</h3>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.textSecondary }}>App Cold Start Duration</span>
                    <h3 style={{ margin: 0, color: theme.colors.accent }}>1.45 sec (Target &lt; 2s)</h3>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Gateway Login Latency</span>
                    <h3 style={{ margin: 0, color: theme.colors.accent }}>0.78 sec (Target &lt; 1s)</h3>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Dashboard Sync Latency</span>
                    <h3 style={{ margin: 0, color: theme.colors.accent }}>1.12 sec (Target &lt; 1.5s)</h3>
                  </div>
                </div>
              </Card>

              <Card hoverable={false}>
                <h3>Live App Activity Stream</h3>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontFamily: 'monospace', fontSize: '11px', color: theme.colors.textSecondary, overflowX: 'auto' }}>
                  <div style={{ whiteSpace: 'nowrap' }}>[14:37:12] SUCCESS: Connected to firebase instance 'millish-prod-db' (region: asia-south1).</div>
                  <div style={{ whiteSpace: 'nowrap' }}>[14:37:13] METRICS: Dispatched diagnostic latency trace payload 'app_launch_audit'.</div>
                  <div style={{ whiteSpace: 'nowrap' }}>[14:37:14] SECURE: Enforced forensic watermark overlays on Course ID 'course_lwc01' for student 'dodlaanusha7@gmail.com'.</div>
                  <div style={{ whiteSpace: 'nowrap' }}>[14:37:15] WEBB_API: Synced domain 'dhruvsoft.millish.shop' with branding configurations.</div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'builder' && (
          <div>
            <Header title="Dynamic Website Builder" subtitle="White-label your landing domain and adjust branding parameters easily." />
            <Card hoverable={false}>
              <form style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: theme.spacing.lg }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  <Input label="Custom Domain Brand Name" defaultValue="Dhruvsoft Coaching Academy" />
                  <Input label="Subdomain Config" defaultValue="dhruvsoft" />
                  <Select
                    label="Branding Accent Theme Color"
                    options={[
                      { value: 'NAVY', label: 'Enterprise Midnight Navy (#001C38)' },
                      { value: 'EMERALD', label: 'Tech Emerald Dark (#10B981)' },
                      { value: 'AMETHYST', label: 'Amethyst Deep Lavender (#6366F1)' }
                    ]}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  <TextArea label="Landing Website Description Copy" defaultValue="Dhruvsoft delivers high-caliber exam prep syllabus with state-of-the-art secure DRM video streaming, dynamic mock-test portals, and personalized AI academic mentoring." />
                  <Button type="button" style={{ marginTop: 'auto' }}>Re-publish & Sync Portal 🌐</Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <Header title="Global Workspace Settings" subtitle="Provision credential keys and configure security bindings." />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: theme.spacing.lg }}>
              <Card hoverable={false}>
                <h3>Environment Configuration Keys (BuildConfig slots)</h3>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  <Input type="password" label="Firebase Project API Key" defaultValue="AIzaSyA1B2C3D4E5F6G7H8I9J0K" />
                  <Input type="password" label="Razorpay Sandbox Key ID" defaultValue="rzp_test_12345ABCDE" />
                  <Input type="password" label="Razorpay Secret Signature" defaultValue="••••••••••••••••••••••••••••••••" />
                </div>
                <Button style={{ marginTop: theme.spacing.md }}>Commit Changes to Secure Vault</Button>
              </Card>

              <Card hoverable={false}>
                <h3>Client Preferences</h3>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  <Switch checked={isDarkMode} onChange={setIsDarkMode} label="Enforce Dark Slate Canvas" />
                  <Switch checked={true} onChange={() => {}} label="Enforce Device Bound Validation" />
                  <Switch checked={true} onChange={() => {}} label="Enforce Forensic Watermark Overlays" />
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div>
            <Header title="Notification Orchestrations" subtitle="Trigger automated emails, transactional WhatsApp messages, and FCM pushes." />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: theme.spacing.lg }}>
              <Card>
                <h3>WhatsApp Business</h3>
                <p style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Delivers transaction confirmations and attendance notices instantly.</p>
                <Divider />
                <Badge variant="success">Active API Gateway</Badge>
              </Card>
              <Card>
                <h3>Email Alert (SMTP)</h3>
                <p style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Dispatches password resets and course syllabus files via Sendinblue.</p>
                <Divider />
                <Badge variant="success">Active SMTP Connect</Badge>
              </Card>
              <Card>
                <h3>Push Alerts (FCM)</h3>
                <p style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Pushes live classroom notices and test timings to student tablets.</p>
                <Divider />
                <Badge variant="success">Topic Mapping OK</Badge>
              </Card>
            </div>
          </div>
        )}

        <Footer />
      </main>
    </div>
  );
};

export default Workspace;
